/**
 * NotificationContext
 * Provides real-time notification state and methods to all components
 */

import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
} from 'react';
import { useAuth } from './AuthContext';
import { useNotificationHub } from '../hooks/useNotificationHub';
import {
  Notification,
  NotificationContextValue,
  ConnectionState,
  ToastNotification,
} from '../types/notification';

// Default context value
const defaultContextValue: NotificationContextValue = {
  isConnected: false,
  connectionState: 'disconnected',
  unreadCount: 0,
  notifications: [],
  connect: async () => {},
  disconnect: async () => {},
  markNotificationRead: async () => {},
  markAllAsRead: async () => {},
  clearNotifications: () => {},
  addToast: () => {},
};

// Create the context
const NotificationContext = createContext<NotificationContextValue>(defaultContextValue);

// Toast queue for managing toast notifications
interface ToastQueueItem extends ToastNotification {
  timeoutId?: NodeJS.Timeout;
}

interface NotificationProviderProps {
  children: React.ReactNode;
  maxToasts?: number;
  defaultToastDuration?: number;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
  maxToasts = 5,
  defaultToastDuration = 5000,
}) => {
  const { state: authState } = useAuth();
  const [toasts, setToasts] = useState<ToastQueueItem[]>([]);
  const toastIdCounter = useRef(0);
  // Mirror the latest toasts so the unmount-only cleanup can clear timers
  // without depending on `toasts` (a [toasts]-dep cleanup would clear every
  // pending auto-dismiss timer whenever any toast is added/removed).
  const toastsRef = useRef<ToastQueueItem[]>([]);
  useEffect(() => {
    toastsRef.current = toasts;
  }, [toasts]);

  // Callback for when notifications are received
  const handleNotificationReceived = useCallback(
    (notification: Notification) => {
      // Create a toast for the new notification
      const toastId = `toast-${toastIdCounter.current++}`;
      const toast: ToastQueueItem = {
        ...notification,
        id: toastId,
        duration: defaultToastDuration,
      };

      setToasts((prev) => {
        // Limit the number of toasts
        const newToasts = [toast, ...prev].slice(0, maxToasts);
        return newToasts;
      });

      // Set up auto-dismiss
      const timeoutId = setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== toastId));
      }, defaultToastDuration);

      // Store the timeout ID for cleanup
      setToasts((prev) =>
        prev.map((t) => (t.id === toastId ? { ...t, timeoutId } : t))
      );
    },
    [defaultToastDuration, maxToasts]
  );

  // Callback for connection state changes
  const handleConnectionStateChange = useCallback((_state: ConnectionState) => {
    // Connection state handled internally
  }, []);

  // Use the notification hub hook
  const {
    isConnected,
    connectionState,
    notifications,
    unreadCount,
    connect,
    disconnect,
    markNotificationRead,
  } = useNotificationHub({
    autoConnect: false,
    onNotificationReceived: handleNotificationReceived,
    onConnectionStateChange: handleConnectionStateChange,
  });

  // Auto-connect when user is authenticated
  useEffect(() => {
    if (authState.isAuthenticated && !authState.isLoading) {
      connect();
    }
  }, [authState.isAuthenticated, authState.isLoading, connect]);

  // Auto-disconnect on logout
  useEffect(() => {
    if (!authState.isAuthenticated && !authState.isLoading) {
      disconnect();
    }
  }, [authState.isAuthenticated, authState.isLoading, disconnect]);

  // Clean up any pending auto-dismiss timers on unmount ONLY. Reads the latest
  // toasts from a ref so this effect never re-runs on toast changes (which would
  // otherwise cancel in-flight 5s auto-dismiss timers).
  useEffect(() => {
    return () => {
      toastsRef.current.forEach((toast) => {
        if (toast.timeoutId) {
          clearTimeout(toast.timeoutId);
        }
      });
    };
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    // Mark each notification as read
    const unreadNotifications = notifications.filter((n) => !n.isRead);
    await Promise.all(
      unreadNotifications.map((n) => markNotificationRead(n.id))
    );
  }, [notifications, markNotificationRead]);

  // Clear all notifications from local state
  const clearNotifications = useCallback(() => {
    setToasts([]);
  }, []);

  // Manually add a toast notification
  const addToast = useCallback(
    (notification: Omit<Notification, 'id' | 'createdAt' | 'isRead'>) => {
      const toastId = `toast-${toastIdCounter.current++}`;
      const toast: ToastQueueItem = {
        ...notification,
        id: toastId,
        createdAt: new Date().toISOString(),
        isRead: false,
        duration: defaultToastDuration,
      };

      setToasts((prev) => {
        const newToasts = [toast, ...prev].slice(0, maxToasts);
        return newToasts;
      });

      // Set up auto-dismiss
      const timeoutId = setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== toastId));
      }, defaultToastDuration);

      // Store the timeout ID
      setToasts((prev) =>
        prev.map((t) => (t.id === toastId ? { ...t, timeoutId } : t))
      );
    },
    [defaultToastDuration, maxToasts]
  );

  // Dismiss a specific toast
  const dismissToast = useCallback((toastId: string) => {
    setToasts((prev) => {
      const toast = prev.find((t) => t.id === toastId);
      if (toast?.timeoutId) {
        clearTimeout(toast.timeoutId);
      }
      return prev.filter((t) => t.id !== toastId);
    });
  }, []);

  // Context value
  const contextValue = useMemo<NotificationContextValue>(
    () => ({
      isConnected,
      connectionState,
      unreadCount,
      notifications,
      connect,
      disconnect,
      markNotificationRead,
      markAllAsRead,
      clearNotifications,
      addToast,
    }),
    [
      isConnected,
      connectionState,
      unreadCount,
      notifications,
      connect,
      disconnect,
      markNotificationRead,
      markAllAsRead,
      clearNotifications,
      addToast,
    ]
  );

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      {/* Toast container is rendered here for accessibility */}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </NotificationContext.Provider>
  );
};

// Toast container component (internal)
interface ToastContainerProps {
  toasts: ToastQueueItem[];
  onDismiss: (id: string) => void;
}

const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onDismiss }) => {
  if (toasts.length === 0) return null;

  return (
    <div
      role="region"
      aria-label="Notifications"
      aria-live="polite"
      className="fixed top-4 right-4 z-50 flex flex-col gap-2 pointer-events-none"
      style={{ maxWidth: '400px' }}
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          role="alert"
          className="pointer-events-auto"
        >
          {/* Toast content is rendered by NotificationToast component */}
          <ToastPlaceholder toast={toast} onDismiss={() => onDismiss(toast.id)} />
        </div>
      ))}
    </div>
  );
};

// Simple toast placeholder (will be replaced by NotificationToast)
const ToastPlaceholder: React.FC<{
  toast: ToastQueueItem;
  onDismiss: () => void;
}> = ({ toast, onDismiss }) => {
  const typeColors: Record<string, string> = {
    success: 'bg-green-500',
    warning: 'bg-yellow-500',
    info: 'bg-blue-500',
    error: 'bg-red-500',
    booking: 'bg-purple-500',
    payment: 'bg-emerald-500',
    system: 'bg-gray-500',
  };

  return (
    <div
      className={`${
        typeColors[toast.type] || typeColors.info
      } text-white p-4 rounded-lg shadow-lg cursor-pointer transition-all hover:opacity-90`}
      onClick={onDismiss}
    >
      <div className="font-semibold">{toast.title}</div>
      <div className="text-sm opacity-90">{toast.message}</div>
    </div>
  );
};

// Custom hook to use the notification context
export const useNotifications = (): NotificationContextValue => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export default NotificationContext;
